import { AnalysisHistoryList } from "@/components/analysis/analysis-history-list";
import { TranscriptAnalyzer } from "@/components/analysis/transcript-analyzer";
import { VoiceRecorder } from "@/components/analysis/voice-recorder";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/agents/analyst/meetings` — admin-only. All processed meetings (analysed
 * transcripts); each opens its full analysis. Top-right "Add meeting" opens
 * the transcript analyser in a modal; new suggestions land in the queue.
 *
 * E2E behaviour contract lives in `e2e/analysis-meetings.spec.ts`.
 */

export function MeetingsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [open, setOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);

  const isAdmin = user.globalRole === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/" />;

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.meetings.title")}</h2>
          <p className="text-muted-foreground mt-1">{t("agentTabs.meetings.intro")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button type="button" size="sm" data-testid="add-meeting-open">
              {t("agentTabs.meetings.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("agentTabs.meetings.dialogTitle")}</DialogTitle>
            </DialogHeader>
            <TranscriptAnalyzer
              defaultOpen
              onAnalyzed={() => {
                // Keep the modal open so the result stays visible; refresh the
                // list underneath. The admin closes the modal when done.
                toast({ title: t("agentTabs.meetings.added") });
                setReloadKey((k) => k + 1);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <VoiceRecorder projects={projects} onCompleted={() => setReloadKey((k) => k + 1)} />

      <AnalysisHistoryList
        source="meeting"
        reloadKey={reloadKey}
        emptyLabel={t("agentTabs.meetings.historyEmpty")}
      />
    </div>
  );
}
